export const COLOR_TOKENS = {
  primary: 'hsl(var(--primary))',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  danger: 'hsl(var(--destructive))',
  destructive: 'hsl(var(--destructive))',
  info: 'hsl(var(--info))',
  background: 'hsl(var(--background))',
  card: 'hsl(var(--card))',
  surfaceBase: 'hsl(var(--surface-base))',
  surfaceHover: 'hsl(var(--surface-hover))',
  border: 'hsl(var(--border))',
  textPrimary: 'hsl(var(--foreground))',
  textMuted: 'hsl(var(--muted-foreground))',
} as const;

export type ColorTokenKey = keyof typeof COLOR_TOKENS;

export const THEME_COLOR_KEYS: ReadonlyArray<ColorTokenKey> = [
  'primary',
  'success',
  'warning',
  'danger',
  'info',
  'background',
  'card',
  'surfaceBase',
  'surfaceHover',
  'border',
  'textPrimary',
  'textMuted',
];

export const THEME_COLOR_OPTIONS: ReadonlyArray<{ key: ColorTokenKey; label: string }> = [
  { key: 'primary', label: 'Primary' },
  { key: 'success', label: 'Success' },
  { key: 'warning', label: 'Warning' },
  { key: 'danger', label: 'Destructive' },
  { key: 'info', label: 'Info' },
  { key: 'background', label: 'Background' },
  { key: 'card', label: 'Card' },
  { key: 'surfaceBase', label: 'Surface Base' },
  { key: 'surfaceHover', label: 'Surface Hover' },
  { key: 'border', label: 'Border' },
  { key: 'textPrimary', label: 'Text Primary' },
  { key: 'textMuted', label: 'Text Muted' },
];

const TOKEN_TO_CSS_VAR: Record<ColorTokenKey, string> = {
  primary: '--primary',
  success: '--success',
  warning: '--warning',
  danger: '--destructive',
  destructive: '--destructive',
  info: '--info',
  background: '--background',
  card: '--card',
  surfaceBase: '--surface-base',
  surfaceHover: '--surface-hover',
  border: '--border',
  textPrimary: '--foreground',
  textMuted: '--muted-foreground',
};

const LEGACY_HEX_TO_TOKEN: Record<string, ColorTokenKey> = {
  '#2563eb': 'primary',
  '#10b981': 'success',
  '#f59e0b': 'warning',
  '#ef4444': 'danger',
  '#06b6d4': 'info',
  '#fafafa': 'background',
  '#f8fafc': 'card',
  '#212121': 'textPrimary',
  '#757575': 'textMuted',
};

export function normalizeColorToken(value: string | null | undefined, fallback: ColorTokenKey = 'primary'): ColorTokenKey {
  if (!value) return fallback;

  const normalized = value.trim().toLowerCase();

  if (normalized in COLOR_TOKENS) {
    return normalized as ColorTokenKey;
  }

  if (normalized in LEGACY_HEX_TO_TOKEN) {
    return LEGACY_HEX_TO_TOKEN[normalized];
  }

  return fallback;
}

/**
 * Check if a value is a valid hex color
 */
export function isHexColor(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^#([0-9A-Fa-f]{3}){1,2}$/.test(value.trim());
}

/**
 * Convert a color token or hex value to a CSS color string.
 * Supports both theme tokens (primary, card, etc.) and custom hex colors (#ff5500).
 */
export function colorTokenToCss(token: string | null | undefined, fallback: ColorTokenKey = 'primary'): string {
  // Handle custom hex colors directly
  if (token && isHexColor(token)) {
    return token.trim();
  }
  
  // Handle theme tokens
  const resolved = normalizeColorToken(token, fallback);
  return COLOR_TOKENS[resolved];
}

export function colorTokenToHslComponents(
  token: string | null | undefined,
  fallback: ColorTokenKey = 'primary',
  root: HTMLElement = document.documentElement,
): string {
  const resolved = normalizeColorToken(token, fallback);
  const cssVarName = TOKEN_TO_CSS_VAR[resolved];
  const raw = getComputedStyle(root).getPropertyValue(cssVarName).trim();
  if (raw) return raw;
  return getComputedStyle(root).getPropertyValue(TOKEN_TO_CSS_VAR[fallback]).trim();
}

export function gradientFromTokens(
  fromToken: string | null | undefined,
  toToken: string | null | undefined,
  direction: string = '135deg',
): string {
  const from = colorTokenToCss(fromToken, 'primary');
  const to = colorTokenToCss(toToken, 'info');
  if (direction === 'radial') {
    return `radial-gradient(circle, ${from} 0%, ${to} 100%)`;
  }
  return `linear-gradient(${direction}, ${from} 0%, ${to} 100%)`;
}
